// © Microsoft Corporation. All rights reserved.

import { getLocalDevPrefix, useLocalConfigs } from "../../utilities";

import { ConfigOrigins } from "@msnews/core-contracts";
import { ExperienceIndex } from "@msnews/config-data-types";
import { fetchAsync } from "./BlobFetcher";
import { memoize } from "lodash-es";

/**
 * ConfigIndexService class
 * This is for fetching and parsing the Config Index into an easily consumable form.
 */
export class ConfigIndexService {
    /** null config index fetches to note as error later */
    private failedConfigIndexFetches: string[] = [];

    /** Application Type */
    private appType: string;

    /** Indicates if configs should be served from local repo */
    private configOrigins: ConfigOrigins;

    /**
     * Initialize config index service.
     * @param {appType} appType
     * @param {ConfigOrigins } Indicates if configs should be served from local repo
     */
    constructor(appType: string, configOrigins: ConfigOrigins) {
        this.appType = appType;
        this.configOrigins= configOrigins;
        this.fetchAndParseConfigIndex = memoize(this.fetchAndParseConfigIndex, (...args) => args.join("/"));
    }

    /**
     * This function fetches and parses config index file from azure blob storage.
     * @param {string} configType config type, loc-configs or feature-configs
     * @param {string} configReference config reference: combination of experienceType, instance source and shared namespace.
     * @param {string} hashedFileName config index hashed file name from the manifest file.
     */
    public async fetchAndParseConfigIndex(configType: string, configReference: string, hashedFileName: string): Promise<ExperienceIndex> {
        // In prod scenario, we don't proceed if hashedFileName is empty
        // In local scenario, hashed name could be empty
        // If hashed file name is null/undefined AND it's not local scenario, return undefined
        const isLocalConfigs = useLocalConfigs(configType, this.configOrigins);
        if (!hashedFileName && !isLocalConfigs) {
            return undefined;
        }

        const relativeUrl = isLocalConfigs
            ? `${getLocalDevPrefix(configType, configReference, this.appType)}/index.json`
            : `${configType}/${configReference}/index.json/${hashedFileName}`;

        try {
            return await fetchAsync(relativeUrl, configType, this.configOrigins);
        } catch (error) {
            if (!isLocalConfigs) {
                this.failedConfigIndexFetches.push(error);
            }
        }
    }

    /**
     * Returns the list of failed config index fetches
     */
    public getFailedConfigIndexFetches(): string[] {
        return this.failedConfigIndexFetches;
    }
}